import React, {Fragment, type ReactNode} from 'react';

import cs from 'classnames';
import styled from 'styled-components';

import {FullHTMLLink} from 'app/components/general/FullHTMLLink';
import {AttachTooltip} from 'app/general/overlays';
import StateButton from 'app/general/widgets/StateButton';

const StyledItemLi = styled.li`
    &.active {
        border-color: rgba(255, 255, 255, 0.5);
        a, button:not([disabled]) {
            color #FFF;
        }
    }
`;

export type GenericActionableType = {
  name: string;
  html_url: string;
};

export type ActionButtonProps<T> = {
  item: T;
  disabled: boolean;
  attached: boolean;
  onRemove: (item: T) => void;
  onSelect: (item: T) => Promise<unknown>;
};

export const GenericSidebarItemStructureActionButton = <
  T extends GenericActionableType
>({
  item,
  onRemove,
  disabled,
  onSelect,
  attached,
}: ActionButtonProps<T>) => (
  <Fragment>
    {attached ? (
      <StateButton
        className="btn btn-link p-2"
        disabled={disabled}
        onClick={() => onRemove(item)}
        title=""
      >
        <i className="far fa-fw fa-check-square" />
      </StateButton>
    ) : (
      <StateButton
        className="btn btn-link p-2"
        disabled={disabled}
        onClick={() => onSelect(item)}
        title=""
      >
        <i className="far fa-fw fa-square" />
      </StateButton>
    )}
  </Fragment>
);

export type Props<T> = Omit<ActionButtonProps<T>, 'disabled'> & {
  nameProcessor: (item: T) => ReactNode;
  htmlURLProcessor?: (item: T) => string | undefined;
  disableAndShowTooltipText?: string;
  actionTooltip?: string;
  getActionButtonComponent?: typeof GenericSidebarItemStructureActionButton;
};

export const GenericAttachedItemsSidebarItem = <
  T extends GenericActionableType
>({
  item,
  nameProcessor,
  htmlURLProcessor = (item) => item.html_url,
  onRemove,
  attached,
  actionTooltip,
  disableAndShowTooltipText,
  onSelect,
  getActionButtonComponent = GenericSidebarItemStructureActionButton,
}: Props<T>) => {
  const disabled = disableAndShowTooltipText != null;
  const name = nameProcessor(item);
  const itemLink = htmlURLProcessor(item);
  const tooltipText = disabled ? disableAndShowTooltipText : actionTooltip;

  return (
    <StyledItemLi
      className={cs('list-group-item small py-1 px-2', {active: attached})}
    >
      <div className="d-flex justify-content-between align-items-center">
        {itemLink != null ? (
          <FullHTMLLink
            className={cs('text-truncate py-2', {
              'text-muted': !attached && disabled,
            })}
            html_url={itemLink}
          >
            {name}
          </FullHTMLLink>
        ) : (
          name
        )}
        <AttachTooltip
          placement="bottom"
          tooltipContent={tooltipText}
          trigger={tooltipText != null ? 'hover focus' : ''}
        >
          {(handlers) => (
            <span {...handlers}>
              {getActionButtonComponent({
                disabled,
                item,
                onRemove,
                onSelect,
                attached,
              })}
            </span>
          )}
        </AttachTooltip>
      </div>
    </StyledItemLi>
  );
};
